/**
 * `/org/:orgSlug/feedback/:requestId` — the console's answer to a pasted link.
 *
 * The org shell above this route has already done the two things that used to
 * need their own panels here: an org this account cannot open never reaches the
 * outlet, and a team session that fails to exchange is reported by
 * `OrganizationRoute` before any child mounts. What is left for this route is
 * the request itself, and the auth failures that only show up when the room is
 * opened — a session that expired between the shell and the socket, or a
 * membership revoked since the link was sent.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useStytchB2BClient } from '@stytch/react/b2b';
import type { FeedbackArtifact } from '@nimbalyst/collab-protocol';

import {
  BrowserFeedbackRequestClient,
  type BrowserFeedbackRequestState,
} from '../feedback/BrowserFeedbackRequestClient';
import { FeedbackRequestPage } from '../feedback/FeedbackRequestPage';
import { useFeedbackArtifactPreviews } from '../feedback/useFeedbackArtifactPreviews';
import type { FeedbackDiscussionPost } from '../feedback/FeedbackRequestDiscussion';
import type { BrowserSessionClient, TeamJwt, TeamMemberId, TeamSession } from '../auth/teamJwt';
import type { TeamSummary } from '../api';
import type { RouteTeam } from '../routing';
import { feedbackSubjectDocumentPath } from '../feedback/feedbackSubjectRoute';
import { DocsSurfaceError } from './DocsRoutes';
import type { OrganizationRouteContext } from './workspaceContext';

interface Connection {
  client: BrowserFeedbackRequestClient;
  host: ReturnType<BrowserFeedbackRequestClient['createRespondHost']>;
  postComment: FeedbackDiscussionPost;
}

/**
 * Owns exactly one connection for the request in the URL.
 *
 * The client is built inside the effect rather than memoized beside it: it is
 * destroyed on teardown and cannot be restarted, so a StrictMode double-mount
 * would otherwise hand the second mount a corpse.
 */
function useFeedbackRequestConnection(
  orgId: string,
  memberId: TeamMemberId,
  requestId: string,
  session: BrowserSessionClient,
) {
  const idle = useMemo<BrowserFeedbackRequestState>(() => ({
    workspacePath: '',
    orgId,
    requestId,
    teamMemberId: memberId,
    status: 'idle',
  }), [memberId, orgId, requestId]);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [state, setState] = useState<BrowserFeedbackRequestState>(idle);

  useEffect(() => {
    const client = new BrowserFeedbackRequestClient({
      target: { orgId, requestId },
      session,
      expectedMemberId: memberId,
    });
    setConnection({
      client,
      host: client.createRespondHost(),
      postComment: (clientMutationId, body) => client.comment(clientMutationId, body),
    });
    setState(client.getState());
    const unsubscribe = client.subscribe(setState);
    // `start` reports failures through the state it resolves with; a rejection
    // here only means the client was already torn down.
    void client.start().catch((error) => {
      console.error('[FeedbackRoutes] Feedback request connection ended', error);
    });
    return () => {
      unsubscribe();
      client.destroy();
      setConnection(null);
    };
  }, [memberId, orgId, requestId, session]);

  return {
    state,
    host: connection?.host,
    postComment: connection?.postComment,
  };
}

/**
 * Opening an artifact navigates to it; browser back returns to the request.
 *
 * Current subjects carry their owning project. A legacy subject without one is
 * opened only when the organization has a single unambiguous project.
 */
function useSubjectOpener(team: RouteTeam) {
  const navigate = useNavigate();
  return useCallback((subject: FeedbackArtifact) => {
    const path = feedbackSubjectDocumentPath(team, subject);
    if (path) navigate(path);
  }, [navigate, team]);
}

export function FeedbackRequestRoute() {
  const { team, teamSession, getTeamJwt } = useOutletContext<OrganizationRouteContext>();
  const stytch = useStytchB2BClient();
  const { requestId } = useParams<{ requestId: string }>();
  const onOpenSubject = useSubjectOpener(team);
  return requestId
    ? (
      <FeedbackRequestConnection
        orgName={team.name}
        orgId={teamSession.orgId}
        memberId={teamSession.memberId}
        requestId={requestId}
        session={stytch.session}
        team={team}
        teamSession={teamSession}
        getTeamJwt={getTeamJwt}
        onOpenSubject={onOpenSubject}
        onSignIn={() => {
          // Same shape as the shell's reauthenticate: clear the session and
          // reload this URL, so the login screen re-arms it as the return path.
          void stytch.session.revoke({ forceClear: true }).then(() => {
            window.location.replace(window.location.href);
          });
        }}
      />
    )
    : <DocsSurfaceError kind="no-access" detail="This feedback link does not name a request." />;
}

function FeedbackRequestConnection({
  orgName,
  orgId,
  memberId,
  requestId,
  session,
  team,
  teamSession,
  getTeamJwt,
  onSignIn,
  onOpenSubject,
}: {
  orgName: string;
  orgId: string;
  memberId: TeamMemberId;
  requestId: string;
  session: BrowserSessionClient;
  team: TeamSummary;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  onSignIn: () => void;
  onOpenSubject: (subject: FeedbackArtifact) => void;
}) {
  const { state, host, postComment } = useFeedbackRequestConnection(
    orgId,
    memberId,
    requestId,
    session,
  );
  // Reads the request because the artifacts name the project the previews are
  // resolved through; a request that has not arrived yet simply has none.
  const previews = useFeedbackArtifactPreviews({
    team,
    teamSession,
    getTeamJwt,
    request: state.request ?? null,
  });
  return (
    <FeedbackRequestPage
      orgName={orgName}
      orgId={orgId}
      requestId={requestId}
      state={state}
      host={host}
      postComment={postComment}
      onSignIn={onSignIn}
      onOpenSubject={onOpenSubject}
      previews={previews}
    />
  );
}
